import React from 'react';
import { useTheme } from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, TextField, InputAdornment } from 'edenred-ui';

import { createRules, FormFields } from '@epi-forms/helpers';
import {
  required,
  maxAmount,
  minAmount
} from '@epi-helpers/formHelpers/validators';
import { MoneyAmountFieldProps } from '@epi-models/forms/Controls';
import { toComma } from '@epi-helpers/commaHelper';

export function MoneyAmountField({
  labelKey = 'transfer_money.amount_field_label',
  name = FormFields.MoneyAmount,
  validators,
  maxIntegerDigits = 4
}: MoneyAmountFieldProps) {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const defaultValidators = [required, minAmount, maxAmount];
  const rules = createRules(validators || defaultValidators);

  const {
    field: { ref, value, onChange: setValue },
    fieldState: { error }
  } = useController({
    name,
    control,
    rules
  });

  const onChange = event => {
    const formattedValue = toComma(event.target.value || '');
    const decimalRegex = new RegExp(
      String.raw`^\d{1,${maxIntegerDigits}}(,\d{0,2})?$`
    );

    if (formattedValue === '' || decimalRegex.test(formattedValue)) {
      setValue(formattedValue);
    }
  };

  return (
    <TextField
      label={t(labelKey)}
      value={value}
      id={name}
      error={!!error}
      helperText={error?.message}
      inputRef={ref}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box fontSize={theme.sizeMedium}>&euro;</Box>
          </InputAdornment>
        )
      }}
      onChange={onChange}
    />
  );
}
